// Leads list + detail. Individual leads are generated from the daily series,
// so lead counts match the dashboard. Names are random first/last pairs,
// phones use 555-01xx, emails use example.com.
import { DEMO_CLIENT, MARKETS, MARKET_ZIPS, VERTICALS, dailyRows, iso, nextUrl, pick, rng, round2, today } from '../client';
import { ZIP_FACTS } from '../zipFacts';

const FIRST = ['James', 'Mary', 'Robert', 'Patricia', 'John', 'Jennifer', 'Michael', 'Linda', 'David', 'Elizabeth', 'William', 'Barbara', 'Richard', 'Susan', 'Joseph', 'Jessica', 'Thomas', 'Karen', 'Charles', 'Nancy', 'Daniel', 'Lisa', 'Mark', 'Sandra', 'Donald', 'Ashley', 'Steven', 'Donna', 'Paul', 'Carol', 'Kenneth', 'Michelle', 'Gary', 'Brenda', 'Ronald', 'Debra'];
const LAST = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Miller', 'Davis', 'Wilson', 'Anderson', 'Taylor', 'Thomas', 'Moore', 'Martin', 'Jackson', 'Thompson', 'White', 'Harris', 'Clark', 'Lewis', 'Robinson', 'Walker', 'Young', 'Allen', 'King', 'Wright', 'Scott', 'Hill', 'Green', 'Adams', 'Baker', 'Nelson', 'Carter', 'Mitchell', 'Roberts'];
const STREETS = ['Oak', 'Maple', 'Cedar', 'Pine', 'Elm', 'Willow', 'Birch', 'Hickory', 'Laurel', 'Magnolia', 'Dogwood', 'Chestnut'];
const SUFFIX = ['St', 'Ave', 'Dr', 'Ln', 'Ct', 'Way'];
const WINDOWS_REASON = ['Drafty / energy bills', 'Broken or fogged glass', 'Updating the look', 'Hard to open'];

let cache = null;

function allLeads() {
  if (cache) return cache;
  const leads = [];
  let id = 5000000;
  const r = rng(7);
  dailyRows().forEach((row) => {
    const market = MARKETS.find((m) => m.name === row.market);
    const vertical = VERTICALS.find((v) => v.name === row.vertical);
    for (let i = 0; i < row.leads; i += 1) {
      id += 1;
      const zip = pick(r, MARKET_ZIPS[market.name]);
      const f = ZIP_FACTS[zip] || {};
      const first = pick(r, FIRST);
      const last = pick(r, LAST);
      const hour = 8 + Math.floor(r() * 12); // local 8am–7pm, stored as UTC (+4)
      const minute = Math.floor(r() * 60);
      const created = `${row.date}T${String(hour + 4).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00Z`;
      const roll = r();
      const declined = roll < 0.035;
      const selfBook = !declined && r() < 0.22;
      // Downstream outcome, using the vertical's funnel rates.
      const appt = r() < vertical.apptRate;
      const issued = appt && r() < vertical.issueRate;
      const demoed = issued && r() < 0.9;
      const sold = demoed && r() < vertical.closeRate / 0.9;
      // Sales are kept on the lead's date (that's how the dashboard counts
      // them); the sold date itself is capped at yesterday.
      const sellDay = new Date(`${row.date}T12:00:00`);
      sellDay.setDate(sellDay.getDate() + 3 + Math.floor(r() * 14));
      const yesterday = new Date(today());
      yesterday.setDate(yesterday.getDate() - 1);
      if (sellDay > yesterday) sellDay.setTime(yesterday.getTime());
      leads.push({
        id,
        client_name: DEMO_CLIENT.name,
        date_created: created,
        webhook_sent_datetime: declined ? null : created.replace(/:00Z$/, ':04Z'),
        first_name: first,
        last_name: last,
        phone: `(${zip.startsWith('27') ? '919' : '704'}) 555-01${String(Math.floor(r() * 100)).padStart(2, '0')}`,
        email: `${first.toLowerCase()}.${last.toLowerCase()}${Math.floor(r() * 90) + 10}@example.com`,
        source: 'facebook',
        medium: 'paid_social',
        territory: market.name,
        zip_code: zip,
        city: f.city || '',
        state: f.state || 'NC',
        address: `${100 + Math.floor(r() * 9800)} ${pick(r, STREETS)} ${pick(r, SUFFIX)}`,
        homeowner: true,
        vertical: vertical.name,
        windows_reason: vertical.name === 'Windows' ? pick(r, WINDOWS_REASON) : null,
        self_book_appointment_date_time: selfBook ? `${row.date} ${pick(r, ['10:00 AM', '1:00 PM', '3:30 PM', '6:00 PM'])}` : null,
        success_self_book: selfBook,
        delivery_status: declined ? 'declined' : 'delivered',
        decline_reason: declined ? pick(r, ['duplicate', 'out_of_area']) : null,
        success_post: !declined,
        post_response: declined ? null : JSON.stringify({ status: 'success', id: `CRM-${id}` }),
        last_webhook_status_code: declined ? null : 200,
        campaign_name: `${vertical.name} · ${market.name}`,
        ad_set_name: pick(r, ['Advantage+', 'Broad 35+', 'Broad 55+']),
        ad_name: `${vertical.name} — ${pick(r, ['Before/After', 'Financing', 'Testimonial', 'Seasonal Offer'])}`,
        _appt: appt,
        _issued: issued,
        _demoed: demoed,
        _sold: sold,
        _sold_date: sold ? iso(sellDay) : null,
        _net: sold ? round2(vertical.ticket * (0.7 + r() * 0.6)) : null,
      });
    }
  });
  cache = leads;
  return leads;
}

function backend(lead) {
  if (!lead._appt) return [];
  return [{
    unique_id: `SR-${lead.id}`,
    created_date: lead.date_created.slice(0, 10),
    appointment_date: lead.date_created.slice(0, 10),
    issue: lead._issued,
    demo: lead._demoed,
    sold_date: lead._sold_date,
    gross_amount: lead._net ? (lead._net * 1.08).toFixed(2) : null,
    net_amount: lead._net ? lead._net.toFixed(2) : null,
    status: lead._sold ? 'Sold' : lead._issued ? 'Demo – No Sale' : 'Appointment Set',
  }];
}

const publicFields = (lead) => Object.fromEntries(Object.entries(lead).filter(([k]) => !k.startsWith('_')));

function filterLeads(params) {
  const after = params.get('date_after');
  const before = params.get('date_before');
  const vertical = params.get('vertical');
  const territory = params.get('territory');
  const zip = params.get('zip_code');
  const delivery = params.get('delivery');
  const search = (params.get('search') || '').toLowerCase();
  return allLeads().filter((l) => {
    const d = l.date_created.slice(0, 10);
    if (after && d < after) return false;
    if (before && d > before) return false;
    if (vertical && l.vertical !== vertical && String(VERTICALS.find((v) => v.name === l.vertical)?.id) !== vertical) return false;
    if (territory && l.territory !== territory) return false;
    if (zip && l.zip_code !== zip) return false;
    if (delivery && l.delivery_status !== delivery) return false;
    if (search && !(`${l.phone} ${l.email} ${l.first_name} ${l.last_name}`.toLowerCase().includes(search))) return false;
    return true;
  }).sort((a, b) => (a.date_created < b.date_created ? 1 : -1));
}

export const routes = [
  ['GET', 'dashboard/leads/territory-options', () => ({ territories: MARKETS.map((m) => m.name) })],
  ['POST', 'dashboard/leads/backend-data-batch', ({ body }) => {
    const ids = new Set((body?.lead_ids || []).map(Number));
    return { results: allLeads().filter((l) => ids.has(l.id)).map((l) => ({ id: l.id, related_backend_data: backend(l) })) };
  }, { silent: true }],
  ['GET', 'dashboard/leads/:id', ({ match }) => {
    const lead = allLeads().find((l) => String(l.id) === match.id);
    if (!lead) return { __status: 404, body: { detail: 'Not found.' } };
    return {
      ...publicFields(lead),
      response_message: lead.success_post ? 'Lead accepted' : 'Declined',
      failed_webhook_attempts: 0,
      processed: true,
      webhook_deliveries: {},
      related_backend_data: backend(lead),
    };
  }],
  ['GET', 'dashboard/leads', ({ params }) => {
    const rows = filterLeads(params);
    const page = Number(params.get('page') || 1);
    const size = Number(params.get('page_size') || 50);
    return {
      count: rows.length,
      next: nextUrl('dashboard/leads', params, page, size, rows.length),
      previous: null,
      results: rows.slice((page - 1) * size, page * size).map(publicFields),
    };
  }],
  ['GET', 'dashboard/verticals', () => VERTICALS.map((v) => ({ id: v.id, vertical: v.name, name: v.name }))],
];

export { allLeads };
